import { Directive, OnInit } from '@angular/core'
import {
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  AbstractControl,
} from '@angular/forms'
import { Tag } from 'src/utils/types'
import {
  AllRule,
  ArnRule,
  ExcludeWeakArnRule,
  TagRule,
  ResourceTypeRule,
  ResourceGroupRule,
} from '../account/services/application.service'
import { UiService } from '../services/ui.service'
@Directive()
export class AllRuleControlComponentBase implements OnInit, ControlValueAccessor {
  private tagChangeFunc: any = null

  ruleForm = this.formBuilder.group({
    ruleFormArray: this.formBuilder.array([]),
  })

  private rowId = 0

  constructor(
    protected formBuilder: UntypedFormBuilder,
    protected uiService: UiService
  ) {}

  // Method to be overridden by derived classes to provide the action
  protected getAction(): string {
    return 'include' // Default to include
  }

  private buildRuleEntry(
    tagName: string,
    tagValue: string,
    operator: string = 'is',
    filterType: string = 'tag',
    resourceType: string = '',
    resourceGroup: string = ''
  ) {
    return {
      tagName: tagName,
      tagValue: tagValue,
      operator: operator,
      filterType: filterType,
      resourceType: resourceType,
      resourceGroup: resourceGroup,
      rowId: this.rowId++,
    }
  }

  private buildAllRuleEntry(
    tagName: string,
    tagValue: string,
    j: number,
    operator: string = 'is',
    filterType: string = 'tag',
    resourceType: string = '',
    resourceGroup: string = ''
  ) {
    return {
      tagName: tagName,
      tagValue: tagValue,
      operator: operator,
      filterType: filterType,
      resourceType: resourceType,
      resourceGroup: resourceGroup,
      rowId: j,
    }
  }

  protected emptyRuleLine() {
    return this.formBuilder.group(this.buildRuleEntry('', ''))
  }

  protected emptyAllRuleLine(j: number) {
    return this.formBuilder.group(this.buildAllRuleEntry('', '', j))
  }

  ngOnInit() {
    this.ruleForm.valueChanges.subscribe((val) => {
      if (this.tagChangeFunc) {
        this.tagChangeFunc(this.tags)
      }
    })

    // Watch for operator changes to trigger validation
    this.ruleForm.get('ruleFormArray')?.valueChanges.subscribe(() => {
      setTimeout(() => this.validateAndFixOperators(), 0)
    })
  }

  /** Note that the checkmark matching are also controlled by this getter ARP-3191 */
  get tags() {
    let ruleFormArray = this.ruleForm.get('ruleFormArray') as UntypedFormArray
    let sanitizedArray = ruleFormArray.controls.map((control: AbstractControl) => {
      if (control instanceof UntypedFormGroup && control.get('type')?.value === 'all') {
        // if its an ALL rule construct its content
        let rulesArray = control.get('rules') as UntypedFormArray
        const allRuleObj = {
          type: 'all',
          rules: rulesArray.controls
            .map((ruleControl) => {
              let ruleValue = ruleControl.value
              if (ruleValue.tagName === 'arn') {
                return new ArnRule({ ruleType: ArnRule.ruleType, arns: ruleValue.tagValue.split(',') })
              } else if (ruleValue.tagName === 'excludeWeakArn') {
                return new ExcludeWeakArnRule({
                  ruleType: ExcludeWeakArnRule.ruleType,
                  arns: ruleValue.tagValue.split(','),
                })
              } else if (ruleValue.filterType === 'resourceType' && ruleValue.resourceType) {
                return new ResourceTypeRule({
                  ruleType: ResourceTypeRule.ruleType,
                  resourceType: ruleValue.resourceType,
                  operator: ruleValue.operator || 'is',
                })
              } else if (ruleValue.filterType === 'resourceGroup' && ruleValue.resourceGroup) {
                return new ResourceGroupRule({
                  ruleType: ResourceGroupRule.ruleType,
                  resourceGroup: ruleValue.resourceGroup,
                  operator: ruleValue.operator || 'is',
                })
              } else if (ruleValue.tagName && ruleValue.tagName.length > 0) {
                return new TagRule({
                  ruleType: TagRule.ruleType,
                  name: ruleValue.tagName,
                  value: ruleValue.tagValue?.length > 0 ? ruleValue.tagValue : null,
                  operator: ruleValue.operator || 'is',
                }) // This line will check there is a tagValue, if none assign null to it. This will make the checkbok match for an empty any value.
              }
              return null // Skip empty or invalid rules
            })
            .filter((rule) => rule !== null), // Remove null entries
        }
        // Add action property if this is an exclusion rule
        if (this.getAction && this.getAction() === 'exclude') {
          ;(allRuleObj as any).action = 'exclude'
        }
        return allRuleObj
      } else {
        let ruleValue = control.value
        if (ruleValue.filterType === 'resourceType' && ruleValue.resourceType) {
          const resourceRule = new ResourceTypeRule({
            ruleType: ResourceTypeRule.ruleType,
            resourceType: ruleValue.resourceType,
            operator: ruleValue.operator || 'is',
          })
          // Add action property if this is an exclusion rule
          if (this.getAction && this.getAction() === 'exclude') {
            ;(resourceRule as any).action = 'exclude'
          }
          return resourceRule
        } else if (ruleValue.filterType === 'resourceGroup' && ruleValue.resourceGroup) {
          const resourceGroupRule = new ResourceGroupRule({
            ruleType: ResourceGroupRule.ruleType,
            resourceGroup: ruleValue.resourceGroup,
            operator: ruleValue.operator || 'is',
          })
          // Add action property if this is an exclusion rule
          if (this.getAction && this.getAction() === 'exclude') {
            ;(resourceGroupRule as any).action = 'exclude'
          }
          return resourceGroupRule
        } else if (ruleValue.tagName && ruleValue.tagName.length > 0) {
          const tagObj = {
            tagName: ruleValue.tagName,
            tagValue: ruleValue.tagValue?.length > 0 ? ruleValue.tagValue : null,
            operator: ruleValue.operator || 'is',
          }
          // Add action property if this is an exclusion rule
          if (this.getAction && this.getAction() === 'exclude') {
            ;(tagObj as any).action = 'exclude'
          }
          return tagObj
        } else {
          return null // Return null for invalid/empty rules
        }
      }
    })
    // Filter out invalid or empty rules, ensuring the client displays only valid tag sets
    return sanitizedArray.filter((rule, index) => {
      // Filter out null rules
      if (rule === null) {
        return false
      }

      const formValue = ruleFormArray.at(index).value
      if (formValue.type === 'all') {
        return (rule as any).rules && (rule as any).rules.length > 0
      } else if (rule instanceof ResourceTypeRule) {
        return rule.resourceType && rule.resourceType.length > 0
      } else if (rule instanceof ResourceGroupRule) {
        return rule.resourceGroup && rule.resourceGroup.length > 0
      } else {
        // Only validate as tag rule if filterType is actually 'tag' or not set
        const filterType = formValue.filterType || 'tag'
        return filterType === 'tag' && (rule as any).tagName && (rule as any).tagName.length > 0
      }
    })
  }

  private initializeForm(selectionTags: Array<Tag | AllRule>) {
    let tagArray = selectionTags.map((tag) => {
      if ((tag as AllRule).rules !== undefined) {
        let allRule = tag as AllRule
        let rulesArray = this.formBuilder.array(
          allRule.rules.map((rule) => {
            if (rule instanceof TagRule) {
              return this.formBuilder.group(
                this.buildRuleEntry(rule.tagName, rule.tagValue ?? '', rule.operator ?? 'is')
              )
            } else if (rule instanceof ResourceTypeRule) {
              return this.formBuilder.group(
                this.buildRuleEntry('', '', rule.operator ?? 'is', 'resourceType', rule.resourceType)
              )
            } else if (rule instanceof ResourceGroupRule) {
              return this.formBuilder.group(
                this.buildRuleEntry('', '', rule.operator ?? 'is', 'resourceGroup', '', rule.resourceGroup)
              )
            } else if (rule instanceof ArnRule) {
              return this.formBuilder.group(this.buildRuleEntry('arn', rule.arnSet.join(','), rule.operator))
            } else if (rule instanceof ExcludeWeakArnRule) {
              return this.formBuilder.group(this.buildRuleEntry('excludeWeakArn', rule.arnSet.join(','), rule.operator))
            }
            throw new Error(`Unknown rule type: ${rule.constructor.name}`)
          })
        )

        return this.formBuilder.group({
          type: 'all',
          rowId: this.rowId++,
          rules: rulesArray,
        })
      } else {
        // Handle both regular tags, ResourceTypeRule, and ResourceGroupRule instances
        if (tag instanceof ResourceTypeRule) {
          return this.formBuilder.group(
            this.buildRuleEntry('', '', tag.operator ?? 'is', 'resourceType', tag.resourceType)
          )
        } else if (tag instanceof ResourceGroupRule) {
          return this.formBuilder.group(
            this.buildRuleEntry('', '', tag.operator ?? 'is', 'resourceGroup', '', tag.resourceGroup)
          )
        } else {
          let regularTag = tag as Tag
          return this.formBuilder.group(
            this.buildRuleEntry(regularTag.tagName, regularTag.tagValue ?? '', (regularTag as any).operator ?? 'is')
          )
        }
      }
    })
    this.ruleForm.setControl('ruleFormArray', this.formBuilder.array(tagArray))
  }

  private ensureEmptyFormLine() {
    const ruleFormArray = this.ruleForm.get('ruleFormArray') as UntypedFormArray
    const tagArray = ruleFormArray.value
    if (tagArray.length === 0) {
      // If the array is completely empty, start with an empty OR line
      ruleFormArray.push(this.emptyRuleLine())
    } else {
      const lastLine = tagArray[tagArray.length - 1]
      const isLastLineNotEmpty = lastLine.tagName || lastLine.tagValue

      // Only add an empty OR line if the last line is not empty and it's not an AND group
      if (isLastLineNotEmpty || lastLine.type === 'all') {
        ruleFormArray.push(this.emptyRuleLine())
      }
    }
  }

  /** Removes rows in the OR and AND rows based on rules. */
  removeTagAt(i: number, innerIndex?: number) {
    let ruleFormArray = this.ruleForm.get('ruleFormArray') as UntypedFormArray
    let targetGroup = ruleFormArray.at(i) as UntypedFormGroup

    if (this.isFormGroup(targetGroup) && targetGroup.get('type')?.value === 'all' && typeof innerIndex === 'number') {
      // Handle removal within an AND group
      let rulesArray = targetGroup.get('rules') as UntypedFormArray
      rulesArray.removeAt(innerIndex)

      // If the AND group becomes empty, remove the whole group
      if (rulesArray.length === 0) {
        ruleFormArray.removeAt(i)
      }

      // Only delete the group if there are no rows left after removal
      if (rulesArray.length === 0) {
        ruleFormArray.removeAt(i)
      }
    } else {
      // Handle removal of an OR row or the entire AND group
      ruleFormArray.removeAt(i)
    }

    // Validate operators after removing rows
    setTimeout(() => this.validateAndFixOperators(), 0)
  }

  /**
   * Removes all rows within an AND group at the specified index.
   * If the group exists and is of type 'all', clears its rules array.
   */
  removeAllRowsInGroup(groupIndex: number) {
    const ruleFormArray = this.ruleForm.get('ruleFormArray') as UntypedFormArray
    const group = ruleFormArray.at(groupIndex) as UntypedFormGroup
    if (this.isFormGroup(group) && group.get('type')?.value === 'all') {
      ruleFormArray.removeAt(groupIndex)
    }
  }

  tagTrackBy(i, obj) {
    return obj.rowId
  }

  tagNameTrackBy(i, obj) {
    return obj.key
  }

  tagValueTrackBy(i, obj) {
    return obj
  }

  writeValue(selectionTags: Array<Tag | AllRule>): void {
    if (selectionTags !== undefined) {
      this.initializeForm(selectionTags)
    }
  }

  registerOnChange(fn: any): void {
    this.tagChangeFunc = fn
  }

  registerOnTouched(fn: any): void {
    // nothing to do here - we don't handle touch events
  }

  addOrRow(i: number, innerAndRowIndex?: number) {
    const ruleFormArray = this.ruleForm.get('ruleFormArray') as UntypedFormArray
    if (i === -1) {
      // add first line
      ruleFormArray.insert(i + 1, this.emptyRuleLine())
      return
    }

    if (innerAndRowIndex === undefined) {
      // add at the end
      ruleFormArray.insert(i + 1, this.emptyRuleLine())
    } else {
      // add in group
      const rulesArray = ruleFormArray.at(i).get('rules') as UntypedFormArray
      rulesArray.push(this.emptyAllRuleLine(rulesArray.length))
    }

    // Validate operators after adding new rows
    setTimeout(() => this.validateAndFixOperators(), 0)
  }

  addAndGroup(i: number) {
    const ruleFormArray = this.ruleForm.get('ruleFormArray') as UntypedFormArray

    if (i === -1) {
      // Add first group at the start
      const group = this.formBuilder.group({
        type: 'all',
        rowId: this.rowId++,
        rules: this.formBuilder.array([this.emptyRuleLine()]),
      })
      ruleFormArray.insert(i, group)
      return
    }

    if (i < ruleFormArray.length) {
      const currentRow = ruleFormArray.at(i)
      // Prevent double-wrapping if already a group
      if (this.isFormGroup(currentRow)) {
        // Already a group, do nothing or add a new group after
        const group = this.formBuilder.group({
          type: 'all',
          rowId: this.rowId++,
          rules: this.formBuilder.array([this.emptyRuleLine()]),
        })
        ruleFormArray.insert(i + 1, group)
        return
      }
      // Convert the row at i into a group with only one row
      const group = this.formBuilder.group({
        type: 'all',
        rowId: this.rowId++,
        rules: this.formBuilder.array([this.formBuilder.group({ ...currentRow.value, rowId: this.rowId++ })]),
      })
      ruleFormArray.insert(i + 1, group)
    } else {
      // Add a new AND group at the end
      const group = this.formBuilder.group({
        type: 'all',
        rowId: this.rowId++,
        rules: this.formBuilder.array([this.emptyRuleLine()]),
      })
      ruleFormArray.insert(i + 1, group)
    }
  }

  isFormGroup(group: AbstractControl): boolean {
    return group instanceof UntypedFormGroup && !!group.get('type')
  }

  getFormArray(control: AbstractControl): UntypedFormArray {
    return control as UntypedFormArray
  }

  getFormGroup(control: AbstractControl): UntypedFormGroup {
    return control as UntypedFormGroup
  }

  canUseIsNotInAndGroup(groupIndex: number, ruleIndex: number): boolean {
    const ruleFormArray = this.ruleForm.get('ruleFormArray') as UntypedFormArray
    const group = ruleFormArray.at(groupIndex)

    if (!this.isFormGroup(group) || group.get('type')?.value !== 'all') {
      return false // Not an AND group
    }

    const rulesArray = group.get('rules') as UntypedFormArray

    // Count how many "is" operators we have (excluding the current rule we're checking)
    const isCount = rulesArray.controls.filter(
      (ruleControl, index) => index !== ruleIndex && ruleControl.get('operator')?.value === 'is'
    ).length

    // "is_not" is only allowed if there's at least one "is" sibling
    return isCount > 0
  }

  disableEnter(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  validateAndFixOperators() {
    const ruleFormArray = this.ruleForm.get('ruleFormArray') as UntypedFormArray

    ruleFormArray.controls.forEach((control, i) => {
      if (this.isFormGroup(control) && control.get('type')?.value === 'all') {
        // Handle AND group rules
        const rulesArray = control.get('rules') as UntypedFormArray

        // Count how many "is" operators we have in this AND group
        const isCount = rulesArray.controls.filter((ruleControl) => ruleControl.get('operator')?.value === 'is').length

        rulesArray.controls.forEach((ruleControl, j) => {
          const operatorControl = ruleControl.get('operator')
          if (operatorControl?.value === 'is_not') {
            // "is_not" in AND group is only allowed if there's at least one "is" sibling
            if (isCount === 0) {
              operatorControl.setValue('is')
            }
          }
        })
      } else {
        // Handle top-level OR rules - "is_not" is NEVER allowed at top level
        const operatorControl = control.get('operator')
        if (operatorControl?.value === 'is_not') {
          operatorControl.setValue('is')
        }
      }
    })
  }
}
